import { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuthDataContext } from "../AuthDataProvider";

const BASE_URL = "https://staging.alertaq.com/api/mfg/";
const ZOOMED_SCALE = 2.5;

function Images({ coreID, focused }) {
  const { user } = useAuthDataContext();
  const [images, setImages] = useState([]);
  const [blobUrls, setBlobUrls] = useState({});
  const [listLoading, setListLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imgScale, setImgScale] = useState(1);
  const [imgTranslate, setImgTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const hasLoaded = useRef(false);
  const hasDragged = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    hasLoaded.current = false;
    setImages([]);
    setSelectedImage(null);
    setBlobUrls(prev => {
      Object.values(prev).forEach(url => URL.revokeObjectURL(url));
      return {};
    });
  }, [coreID]);

  useEffect(() => {
    return () => {
      setBlobUrls(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
    };
  }, []);

  useEffect(() => {
    setImgScale(1);
    setImgTranslate({ x: 0, y: 0 });
  }, [selectedImage]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      if (!hasDragged.current && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        hasDragged.current = true;
      }
      lastPos.current = { x: e.clientX, y: e.clientY };
      setImgTranslate(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    };
    const onMouseUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (!focused || !coreID || !user.token || hasLoaded.current) return;
    hasLoaded.current = true;
    setListLoading(true);

    const authHeaders = { authorization: user.token, admin: "true" };

    fetch(`${BASE_URL}images/?coreID=${encodeURIComponent(coreID)}`, { headers: authHeaders })
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then(body => {
        const seen = new Set();
        const flat = [];
        for (const img of body.list ?? []) {
          if (!seen.has(img._id)) {
            seen.add(img._id);
            flat.push({ imageId: img._id, timestamp: img.timestamp, tags: img.tags ?? [] });
          }
        }
        flat.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setImages(flat);
        setListLoading(false);

        flat.forEach(img => {
          fetch(`${BASE_URL}images/${encodeURIComponent(img.imageId)}`, { headers: authHeaders })
            .then(res => (res.ok ? res.blob() : Promise.reject(res.status)))
            .then(blob => {
              setBlobUrls(prev => ({ ...prev, [img.imageId]: URL.createObjectURL(blob) }));
            })
            .catch(err => console.log("[Images] blob error:", img.imageId, err));
        });
      })
      .catch(err => {
        console.log("[Images] list error:", err);
        setListLoading(false);
      });
  }, [focused, coreID, user.token]);

  const handleImgMouseDown = (e) => {
    if (imgScale === 1) return;
    hasDragged.current = false;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
    e.preventDefault();
  };

  const selectedIndex = selectedImage
    ? images.findIndex(img => img.imageId === selectedImage.imageId)
    : -1;
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex !== -1 && selectedIndex < images.length - 1;

  const showPrev = () => {
    if (hasPrev) setSelectedImage(images[selectedIndex - 1]);
  };
  const showNext = () => {
    if (hasNext) setSelectedImage(images[selectedIndex + 1]);
  };

  useEffect(() => {
    if (!selectedImage) return;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
      else if (e.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedImage, images]);

  const handleImgClick = (e) => {
    if (hasDragged.current) return;
    if (imgScale > 1) {
      setImgScale(1);
      setImgTranslate({ x: 0, y: 0 });
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setImgTranslate({ x: cx * (1 - ZOOMED_SCALE), y: cy * (1 - ZOOMED_SCALE) });
      setImgScale(ZOOMED_SCALE);
    }
  };

  return (
    <div>
      {listLoading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      )}
      {!listLoading && images.length === 0 && hasLoaded.current && (
        <Typography color="textSecondary">No photos found for this device.</Typography>
      )}
      <Grid container spacing={2}>
        {images.map(img => {
          const blobUrl = blobUrls[img.imageId];
          const dateStr = new Date(img.timestamp).toLocaleString();
          const label = img.tags.join(", ") || "Unlabeled";
          return (
            <Grid item key={img.imageId} xs={6} sm={4} md={3}>
              <Tooltip title={`${label} — ${dateStr}`} arrow>
                <div
                  onClick={() => blobUrl && setSelectedImage(img)}
                  style={{
                    cursor: blobUrl ? "pointer" : "default",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    overflow: "hidden",
                    aspectRatio: "4/3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f5f5f5",
                  }}
                >
                  {blobUrl ? (
                    <img
                      src={blobUrl}
                      alt={label}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </div>
              </Tooltip>
              <Typography variant="caption" display="block" align="center" noWrap title={label}>
                {label}
              </Typography>
              <Typography variant="caption" display="block" align="center" color="textSecondary">
                {dateStr}
              </Typography>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={!!selectedImage} onClose={() => setSelectedImage(null)} fullScreen>
        {selectedImage && (
          <>
            <DialogTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span>
                {selectedImage.tags.join(", ") || "Unlabeled"}
                <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 12 }}>
                  {new Date(selectedImage.timestamp).toLocaleString()}
                </Typography>
              </span>
              <Button onClick={() => setSelectedImage(null)} size="small">Close</Button>
            </DialogTitle>
            <DialogContent
              style={{ padding: 0, overflow: "hidden", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}
              onMouseDown={handleImgMouseDown}
              onClick={handleImgClick}
            >
              <img
                src={blobUrls[selectedImage.imageId]}
                alt={selectedImage.tags.join(", ")}
                draggable={false}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transformOrigin: "0 0",
                  transform: `translate(${imgTranslate.x}px, ${imgTranslate.y}px) scale(${imgScale})`,
                  transition: isDragging ? "none" : "transform 0.2s ease",
                  cursor: imgScale > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
                  userSelect: "none",
                }}
              />
              {hasPrev && (
                <IconButton
                  onClick={(e) => { e.stopPropagation(); showPrev(); }}
                  style={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0,0,0,0.4)",
                    color: "#fff",
                  }}
                  size="large"
                >
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>
              )}
              {hasNext && (
                <IconButton
                  onClick={(e) => { e.stopPropagation(); showNext(); }}
                  style={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0,0,0,0.4)",
                    color: "#fff",
                  }}
                  size="large"
                >
                  <ChevronRightIcon fontSize="large" />
                </IconButton>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
}

export default Images;
